import { useState, type ComponentProps, type CSSProperties } from 'react';
import { cn } from './cn';
import { contrastRatio } from './contrast';
import { focusRing } from './focus';

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => [...w][0]!.toUpperCase())
    .join('');
}

const SIZES = { sm: 'h-6 w-6 text-[10px]', md: 'h-7 w-7 text-[11px]', lg: 'h-9 w-9 text-xs', xl: 'h-16 w-16 text-lg' } as const;

/** Initials readable on the person's colour. */
const textOn = (color: string) => (contrastRatio('#ffffff', color) >= 4.5 ? '#ffffff' : '#141414');

/**
 * A profile photo's URL, only made by `avatarPhoto` (lib/avatar.ts) from a checked key: no component takes a raw URL,
 * so no peer can put a tracking pixel in everyone's editor.
 */
export type AvatarPhoto = string & { readonly __avatarPhoto: unique symbol };

/**
 * The photo over the initials: decorative (the disc is named), lazy, and gone on error so the initials show; keyed by
 * its URL so a new photo gets a fresh try.
 */
function Photo({ src }: { src: AvatarPhoto }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return <img src={src} alt="" loading="lazy" decoding="async" draggable={false} onError={() => setFailed(true)} className="absolute inset-0 h-full w-full rounded-full object-cover" />;
}

/** A person's colour disc with their initials, under their photo when they have one, named for assistive tech. */
export function Avatar({ name, color, photo, size = 'md', className }: { name: string; color: string; photo?: AvatarPhoto; size?: keyof typeof SIZES; className?: string }) {
  const text = textOn(color);
  return (
    <span
      role="img"
      aria-label={name}
      title={name}
      data-photo={photo ? '' : undefined}
      className={cn('relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-sans font-semibold ring-2 ring-surface select-none', SIZES[size], className)}
      style={{ background: color, color: text }}
    >
      {initials(name)}
      {photo && <Photo key={photo} src={photo} />}
    </span>
  );
}

/**
 * A person's disc that does something when pressed (follow them, open the members): named by `label`, pressed state
 * as a ring in their colour, 44 px under a coarse pointer.
 */
export function AvatarButton({
  name,
  color,
  photo,
  label,
  pressed,
  size = 'lg',
  className,
  style,
  ...rest
}: Omit<ComponentProps<'button'>, 'color'> & { name: string; color: string; photo?: AvatarPhoto; label: string; pressed?: boolean; size?: keyof typeof SIZES }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      title={name}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-full border-2 border-surface font-sans font-semibold select-none transition-transform duration-(--duration-fast) hover:-translate-y-0.5 aria-pressed:z-10 aria-pressed:scale-110 aria-pressed:ring-[3px] aria-pressed:ring-offset-1 pointer-coarse:size-11',
        focusRing,
        SIZES[size],
        className,
      )}
      style={{ background: color, color: textOn(color), ...(pressed ? ({ '--tw-ring-color': color } as CSSProperties) : {}), ...style }}
      {...rest}
    >
      {initials(name) || '?'}
      {photo && <Photo key={photo} src={photo} />}
    </button>
  );
}
